import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/** How long slideshow scroll events are ignored after the gallery moves the slideshow itself */
const PROGRAMMATIC_SCROLL_GRACE_MS = 1000;

/** Delay before a swiped-to slide selects its variant, so fast swipes only select where they stop */
const SWIPE_SELECT_DELAY_MS = 350;

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    // Capture phase, so selecting a variant can take precedence over opening the zoom dialog
    this.addEventListener('click', this.#handleVariantMediaClick, { signal, capture: true });
    this.addEventListener('pointerdown', this.#handlePointerDown, { signal, capture: true });
    this.addEventListener('slideshow:select', this.#handleSlideshowSelect, { signal });
    // Mobile viewer: a picked swatch shows its image right away; the variant re-render confirms it
    this.closest('.shopify-section')?.addEventListener('change', this.#handleOptionChange, { signal });

    // Mobile viewer: horizontal swipes on the main image step through the product images
    const viewerMain = this.querySelector('.mobile-media-viewer__main');
    viewerMain?.addEventListener('touchstart', this.#handleViewerTouchStart, { signal, passive: true });
    viewerMain?.addEventListener('touchend', this.#handleViewerTouchEnd, { signal, passive: true });

    // The slideshow scrolls to its initial slide on connect; that is not a shopper swipe
    this.#ignoreScrollUntil = performance.now() + PROGRAMMATIC_SCROLL_GRACE_MS;
  }

  #controller = new AbortController();

  /** @type {number} */
  #ignoreScrollUntil = 0;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #swipeTimer;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    clearTimeout(this.#swipeTimer);
  }

  /** @type {{ x: number, y: number } | null} */
  #pointerStart = null;

  /**
   * Remembers where a press started, so a slideshow drag does not count as a media click.
   * @param {PointerEvent} event - The pointerdown event.
   */
  #handlePointerDown = (event) => {
    this.#pointerStart = { x: event.clientX, y: event.clientY };
  };

  /**
   * Selects the options of the variant a clicked media item is attached to (see snippets/media-variant-options).
   * Options the media's variants differ in, such as the pack size, keep the shopper's choice.
   * When the media already matches the selection the click continues as usual, e.g. to zoom.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleVariantMediaClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('dialog, slideshow-controls, slideshow-arrows')) return;

    const start = this.#pointerStart;
    this.#pointerStart = null;
    if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 10) return;

    // Mobile viewer thumbnail: show it as the main image, and select its variant if it has one
    const thumb = event.target.closest('.mobile-media-viewer__thumb');
    if (thumb instanceof HTMLElement && this.contains(thumb)) {
      event.preventDefault();
      event.stopPropagation();
      this.#showInViewer(thumb);
      if (thumb.dataset.variantOptions) this.#selectVariantOptions(thumb);
      return;
    }

    const mediaItem = event.target.closest('[data-variant-options]');
    if (!(mediaItem instanceof HTMLElement) || !this.contains(mediaItem)) return;

    if (this.#selectVariantOptions(mediaItem)) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  /**
   * Mobile slideshow: when the shopper swipes (or taps a dot/arrow) to media attached to a variant,
   * selects that variant's shared options, e.g. the scent. Media without a variant only shows.
   *
   * @param {Event} event - The slideshow:select event.
   */
  #handleSlideshowSelect = (event) => {
    const detail = /** @type {{ userInitiated?: boolean, slide?: Element } | undefined} */ (
      /** @type {CustomEvent} */ (event).detail
    );
    if (!detail?.userInitiated || performance.now() < this.#ignoreScrollUntil) return;

    const slide = detail.slide;
    clearTimeout(this.#swipeTimer);
    if (!(slide instanceof HTMLElement) || !slide.dataset.variantOptions) return;

    this.#swipeTimer = setTimeout(() => {
      if (slide.isConnected) this.#selectVariantOptions(slide);
    }, SWIPE_SELECT_DELAY_MS);
  };

  /**
   * Mobile viewer: swaps the main image for the media of a thumbnail. No scrolling is involved.
   * @param {HTMLElement} thumb - A .mobile-media-viewer__thumb button with the image data attributes.
   */
  #showInViewer(thumb) {
    const image = this.querySelector('.mobile-media-viewer__image');
    if (!(image instanceof HTMLImageElement) || !thumb.dataset.imageSrc) return;

    if (image.dataset.mediaId !== thumb.dataset.mediaId) {
      image.srcset = thumb.dataset.imageSrcset || '';
      image.src = thumb.dataset.imageSrc;
      if (thumb.dataset.imageWidth) image.width = Number(thumb.dataset.imageWidth);
      if (thumb.dataset.imageHeight) image.height = Number(thumb.dataset.imageHeight);
      image.alt = thumb.dataset.imageAlt || '';
      image.dataset.mediaId = thumb.dataset.mediaId || '';
    }

    for (const button of this.querySelectorAll('.mobile-media-viewer__thumb')) {
      if (button === thumb) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    }
  }

  /** @type {{ x: number, y: number } | null} */
  #viewerTouchStart = null;

  /**
   * @param {TouchEvent} event - The touchstart event on the mobile viewer's main image.
   */
  #handleViewerTouchStart = (event) => {
    const touch = event.touches[0];
    this.#viewerTouchStart = event.touches.length === 1 && touch ? { x: touch.clientX, y: touch.clientY } : null;
  };

  /**
   * A clearly horizontal swipe (at least 40px, mostly sideways) shows the next or previous image.
   * Nothing scrolls: the main image is swapped like a thumbnail click, so the shown image is always
   * the one in the list. Media attached to a variant also selects it.
   *
   * @param {TouchEvent} event - The touchend event on the mobile viewer's main image.
   */
  #handleViewerTouchEnd = (event) => {
    const start = this.#viewerTouchStart;
    this.#viewerTouchStart = null;
    const touch = event.changedTouches[0];
    if (!start || !touch) return;

    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;
    if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY) * 1.5) return;

    const thumbs = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('.mobile-media-viewer__thumb')));
    const image = this.querySelector('.mobile-media-viewer__image');
    if (thumbs.length < 2 || !(image instanceof HTMLImageElement)) return;

    const currentIndex = Math.max(
      0,
      thumbs.findIndex((thumb) => thumb.dataset.mediaId === image.dataset.mediaId)
    );
    // Swipe left shows the next image; RTL layouts swap the direction
    const isRtl = getComputedStyle(this).direction === 'rtl';
    const step = (deltaX < 0) !== isRtl ? 1 : -1;
    const nextThumb = thumbs[(currentIndex + step + thumbs.length) % thumbs.length];
    if (!nextThumb) return;

    this.#showInViewer(nextThumb);
    if (nextThumb.dataset.variantOptions) this.#selectVariantOptions(nextThumb);
  };

  /**
   * Mobile viewer: when a variant option changes (e.g. a scent swatch), shows the thumbnail whose
   * variant options match the new value right away.
   * @param {Event} event - The change event from the variant picker.
   */
  #handleOptionChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio' || !input.checked) return;
    if (!input.closest('variant-picker')) return;

    const name = input.dataset.optionName;
    if (!name) return;

    for (const thumb of this.querySelectorAll('.mobile-media-viewer__thumb[data-variant-options]')) {
      if (!(thumb instanceof HTMLElement)) continue;
      try {
        const options = JSON.parse(thumb.dataset.variantOptions || '{}');
        if (options[name] === input.value) {
          this.#showInViewer(thumb);
          return;
        }
      } catch {
        // Ignore thumbnails with unreadable options
      }
    }
  };

  /**
   * Checks the variant picker inputs for the options stored on a media element.
   * Changes one option at a time; the variant update re-renders the gallery.
   *
   * @param {HTMLElement} mediaItem - Element with a data-variant-options JSON object.
   * @returns {boolean} Whether a variant option was changed.
   */
  #selectVariantOptions(mediaItem) {
    const variantPicker = this.closest('.shopify-section')?.querySelector('variant-picker');
    if (!variantPicker) return false;

    /** @type {Record<string, string>} */
    let options;
    try {
      options = JSON.parse(mediaItem.dataset.variantOptions || '{}');
    } catch {
      return false;
    }

    for (const [name, value] of Object.entries(options)) {
      const nameSelector = `[data-option-name="${CSS.escape(name)}"]`;
      const input = variantPicker.querySelector(`input[type="radio"]${nameSelector}[value="${CSS.escape(value)}"]`);

      if (input instanceof HTMLInputElement) {
        if (input.checked) continue;

        input.click();
        return true;
      }

      const select = variantPicker.querySelector(`select:has(option${nameSelector})`);
      if (select instanceof HTMLSelectElement && select.value !== value) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    }

    return false;
  }

  /**
   * Moves the slideshow to the media of the chosen variant, found by media id rather than position.
   * The server renders data-selected-media-id; it is empty until the shopper picks a variant.
   */
  showSelectedMedia() {
    const mediaId = this.dataset.selectedMediaId;
    const slideshow = this.slideshow;
    if (!mediaId || !slideshow) return;

    this.#ignoreScrollUntil = performance.now() + PROGRAMMATIC_SCROLL_GRACE_MS;

    // After the slideshow's own initial positioning, which runs in the next animation frame
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.#ignoreScrollUntil = performance.now() + PROGRAMMATIC_SCROLL_GRACE_MS;
        slideshow.select({ id: mediaId }, undefined, { animate: false });
      });
    });
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
        if (newMediaGallery instanceof MediaGallery) newMediaGallery.showSelectedMedia();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * The zoom dialog follows the grid order, which can differ from the slideshow order, so the
   * slide is found by the zoomed media's id when it has one.
   *
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    const mediaId = this.refs.media?.[event.detail.index]?.dataset.mediaId;
    this.#ignoreScrollUntil = performance.now() + PROGRAMMATIC_SCROLL_GRACE_MS;

    if (mediaId) {
      this.slideshow?.select({ id: mediaId }, undefined, { animate: false });
    } else {
      this.slideshow?.select(event.detail.index, undefined, { animate: false });
    }
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
